import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { Icon, PopMark, type IconName } from './Icon';
import { usePrefs, type ThemePref } from './prefs';
import { useAuth } from '../auth/AuthProvider';
import { initials } from '../lib/format.js';

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  count?: string | number | null;
  /** Exact match only (index routes). */
  end?: boolean;
  /** Override NavLink's own active test (e.g. query-string station links). */
  active?: boolean;
}
export interface NavGroup {
  scope?: string;
  items: NavItem[];
}

/** Ink side nav + main column, per docs/design-review/dash.css. */
export function Shell({
  label,
  brand,
  brandSub,
  header,
  groups,
  roleText,
  showMute,
  extraMenu,
  children,
}: {
  label: string;
  brand: string;
  brandSub?: string;
  header?: ReactNode;
  groups: NavGroup[];
  roleText: string;
  showMute?: boolean;
  extraMenu?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="shell">
      <a className="skip btn" href="#main">
        Skip to content
      </a>
      <nav className="nav" aria-label={label}>
        <div className="brand">
          <PopMark />
          <b>{brand}</b>
          {brandSub ? <span className="sub">{brandSub}</span> : null}
        </div>
        {header}
        {groups.map((g, gi) => (
          <div key={gi} className="stack s4" role="group" aria-label={g.scope}>
            {g.scope ? <div className="scope" aria-hidden="true">{g.scope}</div> : null}
            {g.items.map((it) => (
              <NavLink
                key={it.to + it.label}
                to={it.to}
                end={it.end}
                className={({ isActive }) => ((it.active ?? isActive) ? 'active' : '')}
                aria-current={it.active === undefined ? undefined : it.active ? 'page' : undefined}
                title={it.label}
              >
                <Icon name={it.icon} />
                <span className="lbl">{it.label}</span>
                {it.count !== null && it.count !== undefined && it.count !== 0 && it.count !== '' ? (
                  <span className="count">
                    <span className="sr-only">, </span>
                    {it.count}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </div>
        ))}
        <UserMenu roleText={roleText} showMute={showMute} extra={extraMenu} />
      </nav>
      <main id="main" className="main" tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}

const THEMES: { value: ThemePref; label: string; icon: IconName }[] = [
  { value: 'system', label: 'Match device', icon: 'monitor' },
  { value: 'light', label: 'Light', icon: 'sun' },
  { value: 'dark', label: 'Dark', icon: 'moon' },
];

function UserMenu({ roleText, showMute, extra }: { roleText: string; showMute?: boolean; extra?: ReactNode }) {
  const { me, signOut } = useAuth();
  const { theme, setTheme, muted, setMuted } = usePrefs();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    wrap.current?.querySelector<HTMLElement>('.menu button')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = me?.name || me?.email || 'Signed in';
  return (
    <div className="foot" ref={wrap}>
      {open ? (
        <div className="menu" id="user-menu" role="group" aria-label="Account and display">
          <span className="label">Theme</span>
          <div role="radiogroup" aria-label="Theme" className="stack s4">
            {THEMES.map((t) => (
              <button key={t.value} type="button" role="radio" aria-checked={theme === t.value} className="menu-item" onClick={() => setTheme(t.value)}>
                <Icon name={t.icon} />
                {t.label}
              </button>
            ))}
          </div>
          {showMute ? (
            <>
              <div className="menu-sep" />
              <button type="button" role="switch" aria-checked={!muted} className="menu-item" onClick={() => setMuted(!muted)}>
                <Icon name={muted ? 'bellOff' : 'bell'} />
                New-order sound {muted ? 'off' : 'on'}
              </button>
            </>
          ) : null}
          {extra}
          <div className="menu-sep" />
          <button
            type="button"
            className="menu-item"
            onClick={async () => {
              await signOut();
              navigate('/sign-in', { replace: true });
            }}
          >
            <Icon name="logout" />
            Sign out
          </button>
        </div>
      ) : null}
      <button ref={btn} type="button" aria-expanded={open} aria-controls={open ? 'user-menu' : undefined} onClick={() => setOpen((o) => !o)} aria-label={`Account: ${name}`}>
        <span className="avatar" aria-hidden="true">
          {initials(me?.name, me?.email)}
        </span>
        <span className="stack tight who">
          <b className="t-label">{name}</b>
          <span className="role">{roleText}</span>
        </span>
      </button>
    </div>
  );
}
